import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Lock, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { updatePost } from '../api/posts';
import { creditsToEur } from '../lib/currency';
import useCreatorPriceOptions from '../hooks/useCreatorPriceOptions';

const MAX_CAPTION = 10_000;
const MIN_PREVIEW = 10;
const MAX_PREVIEW = 500;

/**
 * Owner-only edit of a published post (caption, location; for paid
 * posts also price + public teaser). Media and paid flag are immutable.
 */
const EditPostDialog = ({ post, open, onOpenChange, onUpdated }) => {
  const { toast } = useToast();
  const [caption, setCaption] = useState('');
  const [location, setLocation] = useState('');
  const [price, setPrice] = useState('');
  const [teaser, setTeaser] = useState('');
  const [saving, setSaving] = useState(false);
  const { credits: priceCredits } = useCreatorPriceOptions(`post_${post?.media_type || 'image'}`);
  const paidOptions = priceCredits.filter((c) => c > 0);

  useEffect(() => {
    if (open && post) {
      setCaption(post.caption || '');
      setLocation(post.location || '');
      setPrice(String(post.price || ''));
      setTeaser(post.preview_description || '');
    }
  }, [open, post]);

  const save = async () => {
    if (post.is_paid) {
      const t = teaser.trim();
      if (t.length < MIN_PREVIEW) {
        toast({
          title: 'Anteprima troppo corta',
          description: `L'anteprima pubblica deve avere almeno ${MIN_PREVIEW} caratteri.`,
          variant: 'destructive',
        });
        return;
      }
    }
    setSaving(true);
    try {
      const patch = { caption, location };
      if (post.is_paid) {
        patch.price = parseInt(price, 10) || post.price;
        patch.preview_description = teaser.trim();
      }
      const updated = await updatePost(post.id, patch);
      toast({ title: 'Post aggiornato' });
      onUpdated?.(updated);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      toast({
        title: 'Impossibile aggiornare',
        description: typeof detail === 'string' ? detail.split('|').pop() : 'Riprova più tardi.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-post-dialog">
        <DialogHeader>
          <DialogTitle>Modifica post</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground">Didascalia</label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, MAX_CAPTION))}
              className="min-h-[110px] resize-none text-sm"
              maxLength={MAX_CAPTION}
              disabled={saving}
              data-testid="edit-post-caption"
            />
          </div>

          <div>
            <label className="mb-1 flex items-center gap-1 text-xs font-semibold text-foreground">
              <MapPin className="h-3.5 w-3.5" /> Luogo
            </label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="h-9 text-sm"
              disabled={saving}
              data-testid="edit-post-location"
            />
          </div>

          {post.is_paid && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold text-foreground">Prezzo (Crediti)</label>
                <select
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  disabled={saving}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="edit-post-price"
                >
                  {!paidOptions.includes(parseInt(price, 10)) && price && (
                    <option value={price}>{price} Credits · €{creditsToEur(parseInt(price, 10) || 0).toFixed(2)} (attuale)</option>
                  )}
                  {paidOptions.map((c) => (
                    <option key={c} value={c}>{c} Credits · €{creditsToEur(c).toFixed(2)}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Chi ha già acquistato mantiene l&apos;accesso al prezzo pagato.
                </p>
              </div>
              <div>
                <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <Lock className="h-3.5 w-3.5 gold-text" /> Anteprima pubblica <span className="gold-text">*</span>
                </label>
                <Textarea
                  value={teaser}
                  onChange={(e) => setTeaser(e.target.value.slice(0, MAX_PREVIEW))}
                  className="min-h-[70px] resize-none text-sm"
                  maxLength={MAX_PREVIEW}
                  disabled={saving}
                  data-testid="edit-post-teaser"
                />
                <div className="mt-1 text-right text-[11px] tabular-nums text-muted-foreground">
                  {teaser.length} / {MAX_PREVIEW}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="edit-post-cancel">
            Annulla
          </Button>
          <Button onClick={save} disabled={saving} className="gold-gradient text-black" data-testid="edit-post-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Salva modifiche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPostDialog;
